import { cn } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import { ReactElement } from 'react';

import { BasicClose } from '../../icons/basic';
import { BoxBase } from '../box/BoxBase';
import { Tag, TagProps } from '../tag/tag';
import { Typography } from '../typography/Typography';

type Props = {
  children: ReactElement<any>;
  variant?: 'landscape' | 'square';
  image: ReactElement<any>;
  address: string;
  subaddress: string | string[];
  tags?: Array<{
    text?: string;
    type: TagProps['type'];
  }>;
  price?: number | string;
  priceType?: string;
  intro?: string;
  landscapeUSPs?: Array<string | string[] | undefined>;
  squareUSP?: Array<string>;
  content?: string;
  hasClosingOption?: {
    onClick: () => void;
  }
}

export const OfferCard = ({
  children,
  variant = 'landscape',
  image,
  address,
  subaddress,
  tags,
  price,
  priceType,
  intro,
  landscapeUSPs,
  squareUSP,
  hasClosingOption,
  content,
}: Props) => {
  const isSquare = variant === 'square';

  const cleanTags = tags?.filter(Boolean);
  const cleanSquareUSPs = squareUSP?.filter(Boolean);
  const cleanLandscapeUSPs = landscapeUSPs?.filter(Boolean);

  return (
    <children.type
      {...(children.props as any)}
      aria-label={`${address} - ${subaddress} - €${price} ${priceType}`}
      data-testid="offerCard"
    >
      <BoxBase
        noShadow={hasClosingOption ? true : false}
        className={cn(
          'h-full min-h-[13.375rem] bg-card-offer',
          !hasClosingOption ?
            'hover:shadow-box-hover' :
            !isSquare ?
              'shadow-large' :
              'border-0',
        )}
      >
        <div
          aria-hidden
          className={clsx('group', {
            'grid min-h-[13.375rem] grid-cols-12': !isSquare,
            'flex flex-col h-full': isSquare,
          })}
        >
          <div
            className={clsx('relative overflow-hidden', {
              'col-span-4': !isSquare,
              'shrink-0 aspect-1.5/1 max-h-[184px]': isSquare,
            })}
          >
            <image.type
              {...(image.props as any)}
              className="absolute size-full object-cover"
              data-testid="offerImage"
            />

            {(cleanTags && cleanTags.length > 0) && (
              <div className={cn('absolute left-0 top-0 flex flex-wrap gap-2 p-2', hasClosingOption && 'w-[calc(100%_-_3rem)]')}>
                {cleanTags.map((tag, index) => tag?.text ? (
                  <Tag
                    key={`${tag.text}-${tag.type}-${index}`}
                    type={tag.type}
                    testid={`offerTag-${index}`}
                  >
                    {tag.text}
                  </Tag>
                ) : null)}
              </div>
            )}
            {hasClosingOption?.onClick && isSquare ? (
              <button
                className="absolute right-0 top-0 z-50 h-fit bg-icon-button-enabled p-2 hover:bg-icon-button-hover"
                onClick={(event) => {
                  // make sure the link isn't triggered when using the closing button
                  event.preventDefault();
                  event.stopPropagation();

                  hasClosingOption.onClick();
                }}
              >
                <BasicClose className="size-6" />
              </button>
            ) : null}
          </div>

          <div
            className={clsx({
              'grid gap-4 col-span-8 p-6': !isSquare,
              'h-full p-4': isSquare,
            })}
          >
            <div
              className={clsx('flex justify-between gap-4 max-sm:flex-col', {
                'flex flex-col h-full': isSquare,
              })}
            >
              <div className="flex flex-col gap-1 break-words max-sm:pr-8 max-xs:pr-0">
                <Typography
                  variant="title-body"
                  type="h2"
                  textColorClassname="text-card-offer-title group-hover:text-card-offer-title-hover decoration-2 group-hover:underline"
                  data-testid="offerAddress"
                >
                  {address}
                </Typography>
                <Typography
                  variant="label-regular"
                  textColorClassname="text-card-offer-subtitle"
                  type="p"
                  data-testid="offerSubaddress"
                >
                  {typeof subaddress === 'string'
                    ? subaddress
                    : subaddress.filter(Boolean).join(' • ')
                  }
                </Typography>
              </div>

              <div
                className={clsx({
                  'flex gap-1 flex-col': isSquare,
                })}
              >
                <div className="flex">
                  <div
                    className={clsx('flex w-fit gap-1', {
                      'flex-col max-sm:items-start items-end text-nowrap': !isSquare,
                      'flex-wrap': isSquare,
                    })}
                  >
                    {price ? (
                      <Typography
                        variant="label-large"
                        textColorClassname="text-card-offer-rent"
                        type="h3"
                        data-testid="offerPrice"
                      >
                        {(!isNaN(price as number) && Intl.NumberFormat('nl-NL', {
                          style: 'currency',
                          currency: 'EUR',
                        }).format(price as number).replace(',00', ',-')) || price}
                      </Typography>
                    ) : null}

                    {priceType ? (
                      <Typography
                        variant="content-body"
                        textColorClassname="text-card-offer-rent-subtitle"
                        data-testid="offerPriceType"
                      >
                        {priceType}
                      </Typography>
                    ) : null}
                  </div>

                  {hasClosingOption?.onClick && !isSquare ? (
                    <button
                      className="z-50 -mr-2 -mt-2 ml-4 h-fit bg-icon-button-enabled p-2 hover:bg-icon-button-hover max-sm:absolute max-sm:right-7 max-sm:top-8"
                      onClick={(event) => {
                        // make sure the link isn't triggered when using the closing button
                        event.preventDefault();
                        event.stopPropagation();

                        hasClosingOption.onClick();
                      }}
                    >
                      <BasicClose className="size-6" />
                    </button>
                  ) : null}
                </div>

                {(isSquare && cleanSquareUSPs && cleanSquareUSPs.length > 0) && (
                  <Typography
                    variant="content-body"
                    textColorClassname="text-card-offer-subtitle"
                  >
                    {cleanSquareUSPs.join(' • ')}
                  </Typography>
                )}
              </div>
            </div>

            {(!isSquare && cleanLandscapeUSPs && cleanLandscapeUSPs.length > 0) && (
              <ul
                className="flex list-disc flex-col gap-2 pl-5 text-card-offer-description marker:text-content-bullet-list"
                data-testid="offerUsps"
              >
                {cleanLandscapeUSPs.map((usp, index) => {
                  const cleanUSP = typeof usp === 'string' ? usp : usp?.filter(Boolean).join(' • ');

                  return (
                    <li
                      key={cleanUSP?.toString()}
                      data-testid={`offerUsp-${index}`}
                    >
                      <Typography
                        variant="content-body"
                        textColorClassname="text-card-offer-usp"
                      >
                        {cleanUSP}
                      </Typography>
                    </li>
                  );
                })}
              </ul>
            )}

            {intro ? (
              <div>
                <Typography
                  variant="content-body"
                  textColorClassname="text-woongebouw-card-description line-clamp-3"
                >
                  {intro}
                </Typography>
              </div>
            ) : null}

            {content ? (
              <div>
                <Typography
                  variant="content-body"
                  textColorClassname="text-woongebouw-card-description line-clamp-3"
                  dangerouslySetInnerHTML={content}
                />
              </div>
            ) : null}
          </div>
        </div>
      </BoxBase>
    </children.type>
  );
};
